import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Phone, Filter, Download, Upload, FileText, FileSpreadsheet, FileType2, UserPlus, X } from 'lucide-react';
import CallModal from '../components/CallModal';
import { localizeOutcome, OUTCOME_TKEY } from '../lib/outcomes';
import { extractErrorMessage } from '../lib/errorUtils';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Leads = ({ user, onLogout, theme, onToggleTheme }) => {
  const [leads, setLeads] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [operators, setOperators] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    campaign_id: searchParams.get('campaign_id') || '',
    status: searchParams.get('status') || '',
    outcome: searchParams.get('outcome') || '',
  });
  const [activeCall, setActiveCall] = useState(null);
  const [callStartTime, setCallStartTime] = useState(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportCampaign, setExportCampaign] = useState('all');
  const [exportOperator, setExportOperator] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [reassignTarget, setReassignTarget] = useState('');
  const [reassigning, setReassigning] = useState(false);
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  useEffect(() => {
    setFilters({
      campaign_id: searchParams.get('campaign_id') || '',
      status: searchParams.get('status') || '',
      outcome: searchParams.get('outcome') || '',
    });
  }, [searchParams]);

  useEffect(() => {
    fetchCampaigns();
    fetchLeads();
    if (user.role === 'admin') fetchOperators();
  }, [filters]);

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/campaigns`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const fetchOperators = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOperators(response.data.filter((u) => u.role === 'operatore'));
    } catch (error) {
      console.error('Error fetching operators:', error);
    }
  };

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.campaign_id) params.append('campaign_id', filters.campaign_id);
      if (filters.status) params.append('status', filters.status);
      if (filters.outcome) params.append('outcome', filters.outcome);
      if (search) params.append('search', search);

      const response = await axios.get(`${API}/leads?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(response.data);
      setSelectedLeadIds((prev) => prev.filter((id) => response.data.some((l) => l.id === id)));
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  };

  const handleCallClick = (lead) => {
    navigator.clipboard.writeText(lead.phone).catch(() => {
      const textArea = document.createElement('textarea');
      textArea.value = lead.phone;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
      } catch (err) {
        console.log('Fallback copy failed', err);
      }
      document.body.removeChild(textArea);
    });
    toast.success(t('number_copied').replace('$phone', lead.phone));
    setActiveCall(lead);
    setCallStartTime(new Date());
  };

  const handleCallEnd = async (outcome, note) => {
    if (!outcome || !note) {
      toast.error(t('outcome_required'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const duration = callStartTime ? Math.floor((new Date() - callStartTime) / 1000) : 0;

      await axios.post(
        `${API}/calls`,
        {
          lead_id: activeCall.id,
          outcome,
          note,
          duration,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success(t('call_saved'));
      setActiveCall(null);
      setCallStartTime(null);
      fetchLeads();
    } catch (error) {
      toast.error('Errore durante la registrazione della chiamata');
      console.error('Error recording call:', error);
    }
  };

  const handleSearch = () => {
    fetchLeads();
  };

  // Bulk reassign helpers
  const toggleLeadSelection = (leadId) => {
    setSelectedLeadIds((prev) =>
      prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedLeadIds.length === leads.length && leads.length > 0) {
      setSelectedLeadIds([]);
    } else {
      setSelectedLeadIds(leads.map((l) => l.id));
    }
  };

  const eligibleReassignOperators = () => {
    // If all selected leads share the same campaign, filter operators to those assigned to it.
    const selected = leads.filter((l) => selectedLeadIds.includes(l.id));
    if (selected.length === 0) return operators;
    const campaignIds = Array.from(new Set(selected.map((l) => l.campaign_id).filter(Boolean)));
    if (campaignIds.length !== 1) return operators;
    const camp = campaignIds[0];
    return operators.filter((op) => (op.campaigns || []).includes(camp));
  };

  const handleBulkReassign = async () => {
    if (!reassignTarget) {
      toast.error(t('bulk_reassign_pick_operator'));
      return;
    }
    setReassigning(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/leads/bulk-reassign`,
        { lead_ids: selectedLeadIds, assigned_to: reassignTarget },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const target = operators.find((o) => o.id === reassignTarget);
      toast.success(
        (t('bulk_reassign_success') || 'Reassigned $count leads to $name')
          .replace('$count', response.data.modified ?? selectedLeadIds.length)
          .replace('$name', target?.full_name || '')
      );
      setSelectedLeadIds([]);
      setReassignTarget('');
      setShowReassignDialog(false);
      fetchLeads();
    } catch (error) {
      toast.error(extractErrorMessage(error, t('bulk_reassign_error')));
    } finally {
      setReassigning(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      toast.loading(t('export_started'), { id: 'export' });
      const params = new URLSearchParams();
      params.append('format', exportFormat);
      if (exportCampaign && exportCampaign !== 'all') params.append('campaign_id', exportCampaign);
      if (exportOperator && exportOperator !== 'all') params.append('assigned_to', exportOperator);

      const response = await axios.get(`${API}/export/leads?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const ext = exportFormat === 'xlsx' ? 'xlsx' : exportFormat;
      a.href = url;
      a.download = `leads_${ts}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('export_completed'), { id: 'export' });
      setShowExportDialog(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('export_failed'), { id: 'export' });
    } finally {
      setExporting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      new: { bg: '#0F62FE15', color: '#0F62FE', label: t('status_new') },
      contacted: { bg: '#F1C21B15', color: '#F1C21B', label: t('status_contacted') },
      converted: { bg: '#19803815', color: '#198038', label: t('status_converted') },
    };
    const style = statusStyles[status] || statusStyles.new;
    return (
      <span
        className="px-2 py-1 rounded text-xs font-medium"
        style={{ backgroundColor: style.bg, color: style.color }}
      >
        {style.label}
      </span>
    );
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar
        user={user}
        onLogout={onLogout}
        theme={theme}
        onToggleTheme={onToggleTheme}
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
      />
      <div
        className="flex-1 overflow-y-auto"
        style={{ backgroundColor: theme === 'dark' ? '#0A0A0A' : '#F8F9FA' }}
      >
        <div className="p-6 lg:p-8">
          <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1
                className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2"
                style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}
                data-testid="leads-title"
              >
                {t('leads_title')}
              </h1>
              <p className="text-base" style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>
                {leads.length} {t('leads_total')}
              </p>
            </div>
            {user.role === 'admin' && (
              <Button className="btn-primary" onClick={() => setShowExportDialog(true)} data-testid="export-leads-button">
                <Download size={18} className="mr-2" />
                {t('export')}
              </Button>
            )}
          </div>

          <div
            className="mb-6 p-4 rounded-lg border"
            style={{
              backgroundColor: theme === 'dark' ? '#161616' : '#FFFFFF',
              borderColor: theme === 'dark' ? '#262626' : '#E0E0E0',
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <div className="flex gap-2">
                  <Input
                    placeholder={t('search_placeholder')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    data-testid="leads-search-input"
                  />
                  <Button onClick={handleSearch} className="btn-primary" data-testid="leads-search-button">
                    <Search size={20} />
                  </Button>
                </div>
              </div>
              <div>
                <Select
                  value={filters.outcome || 'all'}
                  onValueChange={(val) => setFilters({ ...filters, outcome: val === 'all' ? '' : val })}
                >
                  <SelectTrigger data-testid="filter-outcome-select">
                    <SelectValue placeholder={t('all_outcomes') || 'Tutti gli esiti'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('all_outcomes') || 'Tutti gli esiti'}</SelectItem>
                    <SelectItem value="__none__">{t('outcome_none') || 'Nessun esito'}</SelectItem>
                    {Object.keys(OUTCOME_TKEY).map((canonical) => (
                      <SelectItem key={canonical} value={canonical}>
                        {localizeOutcome(t, canonical)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Select 
                  value={filters.status || 'all'} 
                  onValueChange={(val) => setFilters({ ...filters, status: val === 'all' ? '' : val })}
                >
                  <SelectTrigger data-testid="filter-status-select">
                    <SelectValue placeholder={t('all_status')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('all_status')}</SelectItem>
                    <SelectItem value="new">{t('status_new')}</SelectItem>
                    <SelectItem value="contacted">{t('status_contacted')}</SelectItem>
                    <SelectItem value="converted">{t('status_converted')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div
            className="rounded-lg border overflow-hidden"
            style={{
              backgroundColor: theme === 'dark' ? '#161616' : '#FFFFFF',
              borderColor: theme === 'dark' ? '#262626' : '#E0E0E0',
            }}
          >
            {user.role === 'admin' && selectedLeadIds.length > 0 && (
              <div
                className="flex items-center justify-between gap-3 px-4 py-3 border-b flex-wrap"
                style={{
                  backgroundColor: theme === 'dark' ? '#0F62FE20' : '#0F62FE10',
                  borderColor: theme === 'dark' ? '#262626' : '#E0E0E0',
                }}
                data-testid="bulk-action-bar"
              >
                <div className="flex items-center gap-3">
                  <span
                    className="text-sm font-semibold"
                    style={{ color: '#0F62FE' }}
                    data-testid="bulk-selected-count"
                  >
                    {selectedLeadIds.length} {t('bulk_selected')}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedLeadIds([])}
                    className="text-xs opacity-70 hover:opacity-100 flex items-center gap-1"
                    style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                    data-testid="bulk-clear-selection"
                  >
                    <X size={14} />
                    {t('bulk_clear')}
                  </button>
                </div>
                <Button
                  className="btn-primary"
                  size="sm"
                  onClick={() => { setReassignTarget(''); setShowReassignDialog(true); }}
                  data-testid="bulk-reassign-open"
                >
                  <UserPlus size={16} className="mr-2" />
                  {t('bulk_reassign_button')}
                </Button>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead
                  style={{
                    backgroundColor: theme === 'dark' ? '#0A0A0A' : '#F8F9FA',
                    borderBottom: `1px solid ${theme === 'dark' ? '#262626' : '#E0E0E0'}`,
                  }}
                >
                  <tr>
                    {user.role === 'admin' && (
                      <th className="px-3 py-3 w-10">
                        <Checkbox
                          checked={leads.length > 0 && selectedLeadIds.length === leads.length}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-leads"
                          aria-label="Select all leads"
                        />
                      </th>
                    )}
                    {(user.role === 'admin' ? [
                      t('col_created'),
                      t('col_name'),
                      t('col_email'),
                      t('col_origin'),
                      t('col_module'),
                      t('col_channel'),
                      t('col_stage'),
                      t('col_owner'),
                      t('col_labels'),
                      t('col_phone'),
                      t('col_phone2'),
                      t('col_whatsapp'),
                      t('col_notes'),
                      t('actions'),
                    ] : [
                      t('col_created'),
                      t('col_name'),
                      t('col_email'),
                      t('col_phone'),
                      t('col_notes'),
                      t('col_outcome'),
                      t('col_owner'),
                      t('actions'),
                    ]).map((label) => (
                      <th
                        key={label}
                        className="px-3 py-3 text-left text-xs uppercase tracking-wider whitespace-nowrap"
                        style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr
                      key={lead.id}
                      style={{ borderBottom: `1px solid ${theme === 'dark' ? '#262626' : '#E0E0E0'}` }}
                      data-testid={`lead-row-${lead.id}`}
                    >
                      {user.role === 'admin' && (
                        <td className="px-3 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedLeadIds.includes(lead.id)}
                            onCheckedChange={() => toggleLeadSelection(lead.id)}
                            data-testid={`select-lead-${lead.id}`}
                            aria-label={`Select lead ${lead.first_name} ${lead.last_name}`}
                          />
                        </td>
                      )}
                      <td
                        className="px-3 py-3 font-mono text-xs whitespace-nowrap"
                        style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                      >
                        {new Date(lead.created_at).toLocaleString(language === 'it' ? 'it-IT' : 'en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td
                        className="px-3 py-3 cursor-pointer whitespace-nowrap"
                        onClick={() => navigate(`/leads/${lead.id}`)}
                        style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}
                        data-testid={`lead-name-${lead.id}`}
                      >
                        <div className="font-medium">
                          {lead.first_name} {lead.last_name}
                        </div>
                      </td>
                      <td
                        className="px-3 py-3 text-xs"
                        style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                      >
                        {lead.email || '-'}
                      </td>
                      {user.role === 'admin' && (
                        <>
                          <td
                            className="px-3 py-3 text-xs whitespace-nowrap"
                            style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                          >
                            {lead.origin || 'A pagamento'}
                          </td>
                          <td
                            className="px-3 py-3 text-xs max-w-xs truncate"
                            style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                            title={lead.form_module || lead.campaign_name}
                          >
                            {lead.form_module || lead.campaign_name}
                          </td>
                          <td
                            className="px-3 py-3 text-xs whitespace-nowrap"
                            style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                          >
                            {lead.channel || 'E-mail'}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <span
                              className="px-2 py-1 rounded text-xs font-medium"
                              style={{ backgroundColor: '#0F62FE15', color: '#0F62FE' }}
                            >
                              {lead.stage || 'Acquisiti'}
                            </span>
                          </td>
                          <td
                            className="px-3 py-3 text-xs whitespace-nowrap"
                            style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                          >
                            {lead.assigned_to_name || 'Unassigned'}
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex flex-wrap gap-1">
                              {(lead.labels || []).map((label) => (
                                <span
                                  key={`${lead.id}-${label}`}
                                  className="px-2 py-0.5 rounded text-xs"
                                  style={{ backgroundColor: '#52525215', color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                                >
                                  {label}
                                </span>
                              ))}
                            </div>
                          </td>
                        </>
                      )}
                      <td className="px-3 py-3 whitespace-nowrap">
                        {user.role === 'admin' ? (
                          <button
                            onClick={() => handleCallClick(lead)}
                            className="font-mono text-xs hover:underline"
                            style={{ color: '#0F62FE' }}
                            data-testid={`call-button-${lead.id}`}
                          >
                            <Phone size={14} className="inline mr-1" />
                            {lead.phone}
                          </button>
                        ) : (
                          <button
                            onClick={() => navigate(`/leads/${lead.id}`)}
                            className="font-mono text-xs hover:underline"
                            style={{ color: '#0F62FE' }}
                            data-testid={`phone-link-${lead.id}`}
                          >
                            <Phone size={14} className="inline mr-1" />
                            {lead.phone}
                          </button>
                        )}
                      </td>
                      {user.role === 'admin' && (
                        <>
                          <td
                            className="px-3 py-3 font-mono text-xs whitespace-nowrap"
                            style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                          >
                            {lead.secondary_phone || '-'}
                          </td>
                          <td
                            className="px-3 py-3 font-mono text-xs whitespace-nowrap"
                            style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                          >
                            {lead.whatsapp_number || lead.phone}
                          </td>
                        </>
                      )}
                      <td
                        className="px-3 py-3 text-xs max-w-xs truncate"
                        style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                        title={lead.facebook_notes || lead.last_note || ''}
                      >
                        {lead.facebook_notes || lead.last_note || '-'}
                      </td>
                      {user.role !== 'admin' && (
                        <>
                          <td
                            className="px-3 py-3 text-xs whitespace-nowrap"
                            data-testid={`outcome-${lead.id}`}
                          >
                            {lead.last_call_outcome ? (
                              <span
                                className="px-2 py-1 rounded text-xs font-medium"
                                style={{ backgroundColor: '#0F62FE15', color: '#0F62FE' }}
                              >
                                {localizeOutcome(t, lead.last_call_outcome)}
                              </span>
                            ) : (
                              <span style={{ color: theme === 'dark' ? '#525252' : '#A8A8A8' }}>-</span>
                            )}
                          </td>
                          <td
                            className="px-3 py-3 text-xs whitespace-nowrap"
                            style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                            data-testid={`owner-${lead.id}`}
                          >
                            {lead.assigned_to_name || 'Unassigned'}
                          </td>
                        </>
                      )}
                      <td className="px-3 py-3">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/leads/${lead.id}`)}
                          data-testid={`view-lead-${lead.id}`}
                        >
                          {t('col_name') === 'Nome' ? 'Dettagli' : 'Details'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {leads.length === 0 && (
            <div className="text-center py-12" data-testid="no-leads-message">
              <p style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>{t('no_leads')}</p>
            </div>
          )}
        </div>
      </div>

      {activeCall && (
        <CallModal
          lead={activeCall}
          startTime={callStartTime}
          onClose={() => setActiveCall(null)}
          onSubmit={handleCallEnd}
          theme={theme}
        />
      )}

      {/* Export Dialog (Admin only) */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('export_dialog_title')}</DialogTitle>
            <DialogDescription>{t('export_dialog_desc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>{t('export_format')}</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {[
                  { key: 'csv', icon: FileText, label: t('export_csv') },
                  { key: 'xlsx', icon: FileSpreadsheet, label: t('export_xlsx') },
                  { key: 'pdf', icon: FileType2, label: t('export_pdf') },
                ].map(({ key, icon: Icon, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setExportFormat(key)}
                    data-testid={`export-format-${key}`}
                    className="flex flex-col items-center gap-1 p-3 rounded border transition-all"
                    style={{
                      borderColor: exportFormat === key ? '#0F62FE' : (theme === 'dark' ? '#262626' : '#E0E0E0'),
                      backgroundColor: exportFormat === key ? '#0F62FE15' : 'transparent',
                      color: exportFormat === key ? '#0F62FE' : (theme === 'dark' ? '#A8A8A8' : '#525252'),
                    }}
                  >
                    <Icon size={20} />
                    <span className="text-xs font-medium">{label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>{t('export_filter_campaign')}</Label>
              <Select value={exportCampaign} onValueChange={setExportCampaign}>
                <SelectTrigger className="mt-1" data-testid="export-campaign-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('export_filter_all_campaigns')}</SelectItem>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('export_filter_operator')}</Label>
              <Select value={exportOperator} onValueChange={setExportOperator}>
                <SelectTrigger className="mt-1" data-testid="export-operator-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('export_filter_all_operators')}</SelectItem>
                  {operators.map((op) => (
                    <SelectItem key={op.id} value={op.id}>
                      {op.full_name}
                      {op.campaigns?.[0] && (
                        <span className="opacity-60 ml-1">
                          {' · '}{campaigns.find((c) => c.id === op.campaigns[0])?.name || ''}
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full btn-primary" onClick={handleExport} disabled={exporting} data-testid="export-submit-button">
              <Download size={18} className="mr-2" />
              {exporting ? t('export_started') : t('export_download')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Bulk Reassign Dialog (Admin only) */}
      <Dialog open={showReassignDialog} onOpenChange={(open) => { if (!open) { setShowReassignDialog(false); setReassignTarget(''); } }}>
        <DialogContent className="max-w-md" data-testid="bulk-reassign-dialog">
          <DialogHeader>
            <DialogTitle>{t('bulk_reassign_title')}</DialogTitle>
            <DialogDescription>
              {t('bulk_reassign_desc')} ({selectedLeadIds.length} {t('bulk_selected')})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>{t('bulk_reassign_operator_label')}</Label>
              <Select value={reassignTarget} onValueChange={setReassignTarget}>
                <SelectTrigger className="mt-1" data-testid="bulk-reassign-operator-select">
                  <SelectValue placeholder={t('bulk_reassign_operator_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {eligibleReassignOperators().map((op) => (
                    <SelectItem key={op.id} value={op.id}>
                      {op.full_name}
                      {op.email && (
                        <span className="opacity-60 ml-1">{' · '}{op.email}</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {eligibleReassignOperators().length === 0 && (
                <p
                  className="text-xs mt-2"
                  style={{ color: '#DA1E28' }}
                  data-testid="bulk-reassign-no-operators"
                >
                  {language === 'it'
                    ? 'Nessun operatore assegnato a questa campagna. Assegnane uno dalla pagina Utenti.'
                    : 'No operators assigned to this campaign. Assign one from the Users page first.'}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => { setShowReassignDialog(false); setReassignTarget(''); }}
                data-testid="bulk-reassign-cancel"
              >
                {t('bulk_reassign_cancel')}
              </Button>
              <Button
                className="btn-primary"
                onClick={handleBulkReassign}
                disabled={reassigning || !reassignTarget}
                data-testid="bulk-reassign-confirm"
              >
                <UserPlus size={16} className="mr-2" />
                {t('bulk_reassign_confirm')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Leads;
